export const useDocumentTraversal = (documentId: string, revisionId: string) =>
  defineStore(
    `/documents/${documentId}/revisions/${revisionId}/traversal`,
    () => {
      const mapGlobalPositionToXCoordinates = ref<{ [key: number]: number }>(
        {}
      );

      const mapYCoorinatesToXPositions = ref<{
        [key: number]: { [key: number]: number };
      }>({});

      const mapGlobalPositionToYCoordinates = ref<{ [key: number]: number }>(
        {}
      );

      const xCoordinatesForYCoordinate = (yCoordinate: number) => {
        if (yCoordinate == undefined) return undefined;

        const xCoordinates = Object.keys(
          mapYCoorinatesToXPositions.value[yCoordinate]
        ).map((coordinate) => parseFloat(coordinate));

        const xCoordinateValues = Object.values(xCoordinates);

        // Sort the xCoordinates in ascending order
        xCoordinateValues.sort((a, b) => a - b);
        return xCoordinateValues;
      };

      const allTokenPositions = ref<number[]>([]);

      const mapTokenCoordinates = (
        tokenGlobalPosition: number,
        xCoordinate: number,
        yCoordinate: number
      ) => {
        if (!mapYCoorinatesToXPositions.value[yCoordinate]) {
          mapYCoorinatesToXPositions.value[yCoordinate] = {};
        }

        mapYCoorinatesToXPositions.value[yCoordinate][xCoordinate] =
          tokenGlobalPosition;

        mapGlobalPositionToXCoordinates.value[tokenGlobalPosition] =
          xCoordinate;
        mapGlobalPositionToYCoordinates.value[tokenGlobalPosition] =
          yCoordinate;

        allTokenPositions.value.push(tokenGlobalPosition);
      };

      const getNextLineGlobalPosition = (tokenGlobalPosition: number) => {
        const xCoordinate =
          mapGlobalPositionToXCoordinates.value[tokenGlobalPosition];
        const yCoordinate =
          mapGlobalPositionToYCoordinates.value[tokenGlobalPosition];

        const allYCoordinates = Object.keys(
          mapYCoorinatesToXPositions.value
        ).map((yCoordinate) => parseFloat(yCoordinate));

        allYCoordinates.sort((a, b) => a - b);

        const currentYCoordinateIndex = allYCoordinates.indexOf(yCoordinate);
        const nextYCoordinate = allYCoordinates[currentYCoordinateIndex + 1];

        const nextXCoordinates = xCoordinatesForYCoordinate(nextYCoordinate);

        if (!nextXCoordinates) return undefined;

        // Next we want to find the closest xCoordinate to the current xCoordinate
        const closestXCoordinate = nextXCoordinates.reduce((prev, curr) => {
          return Math.abs(curr - xCoordinate) < Math.abs(prev - xCoordinate)
            ? curr
            : prev;
        });

        return mapYCoorinatesToXPositions.value[nextYCoordinate][
          closestXCoordinate
        ];
      };

      const getPreviousLineGlobalPosition = (tokenGlobalPosition: number) => {
        const xCoordinate =
          mapGlobalPositionToXCoordinates.value[tokenGlobalPosition];
        const yCoordinate =
          mapGlobalPositionToYCoordinates.value[tokenGlobalPosition];

        const allYCoordinates = Object.keys(
          mapYCoorinatesToXPositions.value
        ).map((yCoordinate) => parseFloat(yCoordinate));
        allYCoordinates.sort((a, b) => a - b);

        const currentYCoordinateIndex = allYCoordinates.indexOf(yCoordinate);
        const previousYCoordinate =
          allYCoordinates[currentYCoordinateIndex - 1];

        const previousXCoordinates =
          xCoordinatesForYCoordinate(previousYCoordinate);

        if (!previousXCoordinates) return undefined;

        // Next we want to find the closest xCoordinate to the current xCoordinate
        const closestXCoordinate = previousXCoordinates.reduce((prev, curr) => {
          return Math.abs(curr - xCoordinate) < Math.abs(prev - xCoordinate)
            ? curr
            : prev;
        });

        return mapYCoorinatesToXPositions.value[previousYCoordinate][
          closestXCoordinate
        ];
      };

      const getEndLinePosition = (tokenGlobalPosition: number) => {
        const yCoordinate =
          mapGlobalPositionToYCoordinates.value[tokenGlobalPosition];
        const xCooordinates = xCoordinatesForYCoordinate(yCoordinate);

        if (!xCooordinates) return undefined;

        const lastXCoordinate = xCooordinates[xCooordinates.length - 1];

        return mapYCoorinatesToXPositions.value[yCoordinate][lastXCoordinate];
      };

      const getStartLinePosition = (tokenGlobalPosition: number) => {
        const yCoordinate =
          mapGlobalPositionToYCoordinates.value[tokenGlobalPosition];
        const xCooordinates = xCoordinatesForYCoordinate(yCoordinate);

        if (!xCooordinates) return undefined;

        const firstXCoordinate = xCooordinates[0];
        return mapYCoorinatesToXPositions.value[yCoordinate][firstXCoordinate];
      };

      const getNextTokenPosition = (
        tokenGlobalPosition: number
      ): number | undefined => {
        const currentIndex =
          allTokenPositions.value.indexOf(tokenGlobalPosition);
        return allTokenPositions.value[currentIndex + 1];
      };

      const getPreviousTokenPosition = (
        tokenGlobalPosition: number
      ): number | undefined => {
        const currentIndex =
          allTokenPositions.value.indexOf(tokenGlobalPosition);
        return allTokenPositions.value[currentIndex - 1];
      };

      return {
        mapTokenCoordinates,
        getNextLineGlobalPosition,
        getPreviousLineGlobalPosition,
        getEndLinePosition,
        getStartLinePosition,
        getNextTokenPosition,
        getPreviousTokenPosition,
      };
    }
  );
